import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { LuX, LuMail, LuCircleCheck, LuCircleX, LuLoader, LuTriangleAlert } from "react-icons/lu";

const DOC_TYPES = [
  {
    value: "FALTA_DOCUMENTACION_O_MARGEN",
    label: "Falta documentación o margen",
    description: "Solicita facturas de compra/venta o margen de beneficio",
  },
  {
    value: "FALTA_RECLAMACION",
    label: "Falta reclamación",
    description: "Solicita el documento de reclamación con importe",
  },
  {
    value: "ACLARAR_IMPORTE",
    label: "Aclarar importe que se reclama",
    description: "Pide aclaración sobre el importe reclamado",
  },
  {
    value: "FALTAN_TODOS_DOCUMENTOS",
    label: "Faltan todos los documentos",
    description: "No se pueden ver los documentos, solicita envío completo",
  },
];

const ROW_STATUS = { IDLE: "idle", SENDING: "sending", OK: "ok", ERROR: "error" };

export default function BulkSendDocRequestModal({
  onClose,
  elegibles,
  apiBaseUrl,
  token,
  userId,
  onSuccess,
}) {
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [franquiciaEmails, setFranquiciaEmails] = useState({});
  const [rowStatus, setRowStatus] = useState(() =>
    Object.fromEntries(elegibles.map((s) => [s.id, { status: ROW_STATUS.IDLE, error: null }]))
  );
  const [isSending, setIsSending] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [globalError, setGlobalError] = useState(null);

  const noElegibles = elegibles.length === 0;
  const canClose = !isSending;
  const sentCount = Object.values(rowStatus).filter((r) => r.status === ROW_STATUS.OK).length;
  const errorCount = Object.values(rowStatus).filter((r) => r.status === ROW_STATUS.ERROR).length;

  useEffect(() => {
    if (!elegibles.length) return;
    const fetchEmails = async () => {
      const entries = await Promise.all(
        elegibles.map(async (sin) => {
          if (!sin.delegacion_propietaria) return [sin.id, null];
          try {
            const res = await fetch(`${apiBaseUrl}/franquicias/${sin.delegacion_propietaria}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) return [sin.id, null];
            const data = await res.json();
            return [sin.id, data?.email || null];
          } catch {
            return [sin.id, null];
          }
        })
      );
      setFranquiciaEmails(Object.fromEntries(entries));
    };
    fetchEmails();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = (id, status, error = null) =>
    setRowStatus((prev) => ({ ...prev, [id]: { status, error } }));

  const handleSend = async () => {
    if (isSending || !selectedDocType) return;
    setIsSending(true);
    setGlobalError(null);

    for (let i = 0; i < elegibles.length; i++) {
      const sin = elegibles[i];
      setStatus(sin.id, ROW_STATUS.SENDING);
      try {
        const res = await fetch(`${apiBaseUrl}/siniestros/${userId}/bulk/send-doc-request`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ids: [sin.id], doc_type: selectedDocType }),
        });
        const payload = await res.json().catch(() => null);
        if (!res.ok) throw new Error(payload?.error || "Error en el servidor");
        if (payload?.sent === 0 && payload?.errors?.length > 0) {
          throw new Error(payload.errors[0]?.error || "No se pudo enviar");
        }

        // Cambiar estado a DOC PEDIDA
        await fetch(`${apiBaseUrl}/siniestros/${userId}/bulk/estado`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ids: [sin.id], estado_siniestro: "DOC PEDIDA" }),
        });

        setStatus(sin.id, ROW_STATUS.OK);
      } catch (err) {
        setStatus(sin.id, ROW_STATUS.ERROR, err?.message || "Error enviando email");
      }

      if (i < elegibles.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 800));
      }
    }

    setIsSending(false);
    setIsDone(true);
    onSuccess?.();
  };

  return (
    <div
      className="fixed inset-0 z-30 overflow-y-auto bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-center min-h-screen px-4 text-center">
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div className="relative inline-block w-full max-w-2xl p-7 overflow-hidden text-left align-middle transition-all transform bg-white border-2 shadow-xl outline-none sm:my-8 rounded-2xl border-[#FCAE52]">

          {/* Header */}
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border-[3px] border-[#FCAE52]">
                <LuMail className="text-gray-700" size={20} />
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-800">Solicitar documentación</p>
                <p className="text-sm text-gray-500">
                  {noElegibles
                    ? "Ningún registro elegible"
                    : `${elegibles.length} siniestro${elegibles.length !== 1 ? "s" : ""} seleccionado${elegibles.length !== 1 ? "s" : ""}`}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={!canClose}
              className="flex items-center justify-center p-1.5 text-gray-400 transition-colors duration-200 bg-transparent rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <LuX size={22} />
            </button>
          </div>

          {noElegibles ? (
            <div className="flex items-start gap-3 mb-5 px-4 py-3.5 bg-amber-50 border border-amber-200 rounded-lg">
              <LuTriangleAlert size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800 mb-1">Sin registros elegibles</p>
                <p className="text-xs text-amber-700">
                  Solo se pueden enviar solicitudes a siniestros en estado <strong>PENDIENTE DOC MRW</strong>.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Selector de tipo de documento — solo antes de enviar */}
              {!isDone && (
                <div className="mb-5">
                  <p className="text-sm font-semibold text-gray-700 mb-3">
                    Selecciona el tipo de documentación a solicitar:
                  </p>
                  <div className="grid grid-cols-1 gap-2">
                    {DOC_TYPES.map((dt) => (
                      <button
                        key={dt.value}
                        type="button"
                        onClick={() => setSelectedDocType(dt.value)}
                        disabled={isSending}
                        className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${
                          selectedDocType === dt.value
                            ? "border-[#FCAE52] bg-amber-50"
                            : "border-gray-200 hover:border-gray-300 bg-white"
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <p className={`text-sm font-semibold ${selectedDocType === dt.value ? "text-[#b87d2a]" : "text-gray-700"}`}>
                          {dt.label}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">{dt.description}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Tabla de siniestros */}
              <div className="mb-4 overflow-hidden border border-gray-200 rounded-lg">
                <table className="w-full text-sm table-fixed">
                  <colgroup>
                    <col className="w-[20%]" />
                    <col className="w-[22%]" />
                    <col className="w-[38%]" />
                    <col className="w-[20%]" />
                  </colgroup>
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">ID Interno</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Ref. Albarán</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Email destino</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</th>
                    </tr>
                  </thead>
                </table>
                <div className="overflow-y-auto" style={{ maxHeight: "260px" }}>
                  <table className="w-full text-sm table-fixed">
                    <colgroup>
                      <col className="w-[20%]" />
                      <col className="w-[22%]" />
                      <col className="w-[38%]" />
                      <col className="w-[20%]" />
                    </colgroup>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {elegibles.map((sin) => {
                        const rs = rowStatus[sin.id];
                        const emailDestino = franquiciaEmails[sin.id];
                        const emailLoaded = sin.id in franquiciaEmails;
                        return (
                          <tr key={sin.id}>
                            <td className="px-3 py-2 font-medium text-gray-800 truncate">{sin.IDInterno || "—"}</td>
                            <td className="px-3 py-2 text-gray-600 truncate">{sin.ref_albaran || "—"}</td>
                            <td className="px-3 py-2 text-xs">
                              {!emailLoaded ? (
                                <span className="text-gray-400 italic">Cargando...</span>
                              ) : emailDestino ? (
                                <span className="text-blue-600 font-medium break-all">{emailDestino}</span>
                              ) : (
                                <span className="text-red-400 font-medium">Sin email configurado</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <RowStatusBadge status={rs.status} error={rs.error} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Resumen final */}
              {isDone && (
                <div className={`flex items-center gap-2 mb-4 px-3 py-2.5 rounded-lg border text-sm font-semibold ${
                  errorCount === 0
                    ? "bg-green-50 border-green-200 text-green-700"
                    : "bg-amber-50 border-amber-200 text-amber-700"
                }`}>
                  {errorCount === 0 ? <LuCircleCheck size={16} /> : <LuTriangleAlert size={16} />}
                  {sentCount} enviado{sentCount !== 1 ? "s" : ""} correctamente
                  {errorCount > 0 && ` · ${errorCount} con error`}
                </div>
              )}

              {globalError && <p className="mb-3 text-sm text-red-600">{globalError}</p>}
            </>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={!canClose}
              className="h-10 px-5 text-sm font-semibold text-gray-600 transition-colors duration-150 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {isDone ? "Cerrar" : "Cancelar"}
            </button>
            {!noElegibles && !isDone && (
              <button
                type="button"
                onClick={handleSend}
                disabled={isSending || !selectedDocType}
                className="h-10 px-5 text-sm font-semibold text-white transition-colors duration-150 bg-blue-600 border border-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSending ? (
                  <><LuLoader size={15} className="animate-spin" /> Enviando...</>
                ) : (
                  <><LuMail size={15} /> Enviar solicitud</>
                )}
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

function RowStatusBadge({ status, error }) {
  if (status === ROW_STATUS.IDLE) return <span className="text-xs text-gray-400">Pendiente</span>;
  if (status === ROW_STATUS.SENDING) return <span className="flex items-center gap-1 text-xs text-amber-500"><LuLoader size={12} className="animate-spin" /> Enviando...</span>;
  if (status === ROW_STATUS.OK) return <span className="flex items-center gap-1 text-xs text-green-600 font-semibold"><LuCircleCheck size={13} /> Enviado</span>;
  if (status === ROW_STATUS.ERROR) return <span className="flex items-center gap-1 text-xs text-red-500 font-semibold" title={error}><LuCircleX size={13} /> Error</span>;
  return null;
}

RowStatusBadge.propTypes = { status: PropTypes.string, error: PropTypes.string };

BulkSendDocRequestModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  elegibles: PropTypes.arrayOf(PropTypes.object).isRequired,
  apiBaseUrl: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  userId: PropTypes.number.isRequired,
  onSuccess: PropTypes.func,
};